export type TVoiceRecording = {
    audio: Blob,
    filename: string,
};

export default class VoiceRecorder {

    private static readonly MAX_RECORDING_MS = 60000;

    private static readonly MIME_TYPES = [
        "audio/webm;codecs=opus",
        "audio/webm",
        "audio/mp4",
        "audio/mp4;codecs=mp4a.40.2",
    ];

    private mediaRecorder: any|null = null;
    private mediaStream: MediaStream|null = null;
    private chunks: Blob[] = [];
    private mimeType = "";
    private timeoutId: number|null = null;
    private failed = false;

    public isSupported(): boolean {
        return typeof navigator !== "undefined"
            && !!navigator.mediaDevices?.getUserMedia
            && typeof (window as any).MediaRecorder === "function";
    }

    public async start(
        onStopped: (recording: TVoiceRecording|null) => void,
        onError: () => void,
    ): Promise<MediaStream> {
        const stream = await navigator.mediaDevices.getUserMedia({audio: true});
        const mediaRecorderConstructor = (window as any).MediaRecorder;
        const mimeType = this.getSupportedMimeType();
        const recorder = new mediaRecorderConstructor(stream, mimeType ? {mimeType} : undefined);

        this.mediaStream = stream;
        this.mediaRecorder = recorder;
        this.chunks = [];
        this.mimeType = mimeType || recorder.mimeType || "";
        this.failed = false;

        recorder.ondataavailable = (event: any): void => {
            if (event.data && event.data.size > 0) {
                this.chunks.push(event.data);
            }
        };
        recorder.onerror = (): void => {
            this.failed = true;
            onError();
            this.stop();
        };
        recorder.onstop = (): void => {
            const chunks = this.chunks;
            const recordingMimeType = this.mimeType || chunks.find(chunk => !!chunk.type)?.type || "audio/webm";
            const recording = !this.failed && chunks.length > 0
                ? {
                    audio: new Blob(chunks, {type: recordingMimeType}),
                    filename: this.getFilename(recordingMimeType),
                }
                : null;
            this.cleanup();
            onStopped(recording);
        };

        recorder.start();
        this.timeoutId = window.setTimeout(() => this.stop(), VoiceRecorder.MAX_RECORDING_MS);

        return stream;
    }

    public stop(): void {
        if (this.mediaRecorder !== null && this.mediaRecorder.state !== "inactive") {
            this.mediaRecorder.stop();
        }
    }

    public destroy(): void {
        if (this.mediaRecorder !== null) {
            this.mediaRecorder.ondataavailable = null;
            this.mediaRecorder.onerror = null;
            this.mediaRecorder.onstop = null;
        }
        this.cleanup();
    }

    private cleanup(): void {
        if (this.timeoutId !== null) {
            window.clearTimeout(this.timeoutId);
            this.timeoutId = null;
        }
        this.mediaStream?.getTracks().forEach(track => track.stop());
        this.mediaStream = null;
        this.mediaRecorder = null;
        this.chunks = [];
        this.mimeType = "";
        this.failed = false;
    }

    private getSupportedMimeType(): string {
        const mediaRecorderConstructor = (window as any).MediaRecorder;
        if (!mediaRecorderConstructor?.isTypeSupported) {
            return "";
        }

        return VoiceRecorder.MIME_TYPES.find(
            mimeType => mediaRecorderConstructor.isTypeSupported(mimeType)
        ) ?? "";
    }

    private getFilename(mimeType: string): string {
        if (mimeType.includes("mp4")) {
            return "voice-message.mp4";
        }
        if (mimeType.includes("mpeg") || mimeType.includes("mp3")) {
            return "voice-message.mp3";
        }
        if (mimeType.includes("wav")) {
            return "voice-message.wav";
        }
        if (mimeType.includes("ogg")) {
            return "voice-message.ogg";
        }

        return "voice-message.webm";
    }

}
